/**
 * Pure utility functions for EInvoiceCreateDraftModal.
 * Extracted for testability — no Vue/Vuex dependencies.
 */

/**
 * Bucketize API response into { ready, needsInfo, failed, activeTab }.
 * Supports both legacy items[] format and new { errors, draftIds, details } format.
 *
 * @param {object} response - API response data
 * @returns {{ ready: Array, needsInfo: Array, failed: Array, activeTab: number }}
 */
function bucketizeItems(response) {
  const ready = []
  const needsInfo = []
  const failed = []

  // Handle new API format: { successful, failed, errors, draftIds, details }
  if (response && (response.errors || response.draftIds || response.details !== undefined)) {
    // Process errors (failed items)
    ;(response.errors || []).forEach((err) => {
      failed.push({
        billId: err.billId,
        billCode: err.billId?.slice(0, 8) || err.billId, // Use shortened ID as code if no billCode
        reasonCode: err.code,
        errorMessage: err.error, // Direct error message from API
        message: err.error,
      })
    })

    // Process details (successful drafts with info)
    ;(response.details || []).forEach((detail) => {
      const draftId = detail.id || detail.draftId
      const billId = detail.billId || detail.saleInvoiceId
      const billNumber = detail.billNumber ?? null
      const base = {
        billId,
        billNumber,
        draftId,
        billCode: billNumber || (draftId ? `Nháp #${draftId}` : null),
        buyerName: detail.buyerName,
        isDuplicate: detail.isDuplicate ?? false,
        status: detail.status,
      }

      // status === 'draft' means successfully created
      // Also check canIssue for legacy format
      const isSuccess =
        detail.status === 'draft' ||
        detail.status === 'already_created' ||
        (detail.canIssue !== false && !detail.missingFields?.length)

      if (isSuccess) {
        ready.push(base)
      } else {
        needsInfo.push({
          ...base,
          missingFields: detail.missingFields || [],
        })
      }
    })

    // Process draftIds (ready drafts without detailed info - fallback)
    ;(response.draftIds || []).forEach((draftId, index) => {
      // Only add if not already processed in ready or needsInfo from details
      const alreadyInReady = ready.some(
        (r) => r.draftId === draftId || r.draftId === Number(draftId),
      )
      const alreadyInNeedsInfo = needsInfo.some(
        (r) => r.draftId === draftId || r.draftId === Number(draftId),
      )
      if (!alreadyInReady && !alreadyInNeedsInfo) {
        ready.push({
          draftId,
          billCode: `Nháp #${index + 1}`,
          isDuplicate: false,
        })
      }
    })

    const activeTab = failed.length ? 2 : needsInfo.length ? 1 : 0
    return { ready, needsInfo, failed, activeTab }
  }

  // Handle legacy items[] format
  const items = response?.items || response || []
  ;(items || []).forEach((it) => {
    if (it.result === 'FAILED') {
      return failed.push({
        billId: it.saleInvoiceId,
        billCode: it.billCode,
        reasonCode: it.fail?.reasonCode,
        errorMessage: it.fail?.message,
        message: it.fail?.message,
        correlationId: it.fail?.correlationId,
      })
    }
    // SUCCESS (includes duplicates)
    const base = {
      billId: it.saleInvoiceId,
      draftId: it.draftId,
      billCode: it.billCode,
      buyerName: it.buyerName,
      isDuplicate: it.meta?.isDuplicate ?? false,
    }
    if (it.preflight?.canIssue) {
      ready.push(base)
    } else {
      needsInfo.push({
        ...base,
        missingFields: (it.preflight?.errors || []).map((e) => e.fieldPath).filter(Boolean),
      })
    }
  })

  const activeTab = failed.length ? 2 : needsInfo.length ? 1 : 0

  return { ready, needsInfo, failed, activeTab }
}

function normalizeOverrideValue(value) {
  if (value === null || value === undefined) {
    return null
  }

  const normalized = typeof value === 'string' ? value.trim() : String(value).trim()
  return normalized || null
}

const CANONICAL_OVERRIDE_KEYS = Object.freeze([
  'buyerType',
  'customerName',
  'contactPersonName',
  'customerTaxCode',
  'customerAddress',
  'customerEmail',
  'customerPhone',
  'customerIdNumber',
  'paymentMethod',
  'note',
  'currency',
  'exchangeRate',
])

const CANONICAL_BUYER_TYPES = new Set(['ENTERPRISE', 'INDIVIDUAL'])

function normalizeOverrideBuyerType(value) {
  const normalized = normalizeOverrideValue(value)
  if (!normalized) {
    return 'ENTERPRISE'
  }

  const upperValue = String(normalized).toUpperCase()
  return CANONICAL_BUYER_TYPES.has(upperValue) ? upperValue : 'ENTERPRISE'
}

function normalizeOverrideExchangeRate(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Build canonical /drafts/batch override payload from buyer input.
 *
 * Accepts both UI field names (`buyerName`, `buyerTaxCode`) and legacy modal
 * field names (`customerName`, `customerTaxCode`) so the caller can normalize
 * single and batch flows through one contract.
 *
 * @param {object} source
 * @returns {object}
 */
function buildDraftOverride(source = {}) {
  const payload = source && typeof source === 'object' ? source : {}
  const canonicalOverride = {
    buyerType: normalizeOverrideBuyerType(payload.buyerType),
    customerName: normalizeOverrideValue(
      payload.buyerName !== undefined ? payload.buyerName : payload.customerName,
    ),
    contactPersonName: normalizeOverrideValue(payload.contactPersonName),
    customerTaxCode: normalizeOverrideValue(
      payload.buyerTaxCode !== undefined ? payload.buyerTaxCode : payload.customerTaxCode,
    ),
    customerAddress: normalizeOverrideValue(
      payload.buyerAddress !== undefined ? payload.buyerAddress : payload.customerAddress,
    ),
    customerEmail: normalizeOverrideValue(
      normalizeEmailList(payload.buyerEmail !== undefined ? payload.buyerEmail : payload.customerEmail),
    ),
    customerPhone: normalizeOverrideValue(
      payload.buyerPhone !== undefined ? payload.buyerPhone : payload.customerPhone,
    ),
    customerIdNumber: normalizeOverrideValue(
      payload.buyerIdNumber !== undefined ? payload.buyerIdNumber : payload.customerIdNumber,
    ),
    paymentMethod: normalizeOverrideValue(payload.paymentMethod),
    note: normalizeOverrideValue(payload.note),
    currency: normalizeOverrideValue(payload.currency) || 'VND',
    exchangeRate: normalizeOverrideExchangeRate(payload.exchangeRate),
  }

  const normalizedOverride = {}
  CANONICAL_OVERRIDE_KEYS.forEach((key) => {
    normalizedOverride[key] = canonicalOverride[key]
  })

  return normalizedOverride
}

/**
 * Guard logic for "silent toast" mode (mode A).
 * Returns true only when ALL conditions are met for non-disruptive single draft creation.
 *
 * @param {{ isBatch, prefer, modalState, readyCount, needsInfoCount, failedCount, hasHardWarning }} params
 * @returns {boolean}
 */
function computeIsSingleSilent({
  isBatch,
  prefer,
  modalState,
  readyCount,
  needsInfoCount,
  failedCount,
  hasHardWarning,
}) {
  return (
    !isBatch &&
    prefer !== 'FORCE_RESULT' &&
    modalState === 'RESULT_SYNC' &&
    readyCount === 1 &&
    needsInfoCount === 0 &&
    failedCount === 0 &&
    !hasHardWarning
  )
}

/**
 * Adaptive backoff interval for job polling.
 * @param {number} elapsedMs - milliseconds since polling started
 * @returns {number} next polling interval in ms
 */
function computePollingInterval(elapsedMs) {
  if (elapsedMs < 40_000) return 2000
  if (elapsedMs < 180_000) return 5000
  return 10_000
}

// ─── Buyer Info Validation (2 active types in current draft flow) ──────────
import {
  BUYER_VALIDATION,
  BUYER_FIELD_REQUIREMENTS,
  BUYER_VALIDATION_MESSAGES,
  EINVOICE_BUYER_TYPE,
  isValidEmailList,
  normalizeEmailList,
} from '@/utils/einvoice.enum'

/**
 * Validate buyer info based on buyerType.
 * @param {'ENTERPRISE'|'INDIVIDUAL'} buyerType
 * @param {object} buyer
 * @returns {{ valid: boolean, errors: Record<string, string> }}
 */
function validateBuyerInfo(buyerType, buyer, options = {}) {
  const { isWholesale = false } = options
  const errors = {}

  const requirements = BUYER_FIELD_REQUIREMENTS[buyerType] || {}
  const requiredFields = requirements.required || []

  // Check required fields
  requiredFields.forEach((field) => {
    if (!buyer[field]?.trim()) {
      errors[field] = getFieldErrorMessage(buyerType, field, 'required')
    }
  })

  // Bán buôn: địa chỉ luôn bắt buộc (FAST error 8362: có MST nhưng thiếu địa chỉ)
  if (isWholesale && !buyer.customerAddress?.trim()) {
    errors.customerAddress = BUYER_VALIDATION_MESSAGES.required.customerAddress
  }

  // Type-specific validation
  if (buyerType === EINVOICE_BUYER_TYPE.ENTERPRISE) {
    if (
      buyer.customerTaxCode?.trim() &&
      !BUYER_VALIDATION.TAX_CODE.test(buyer.customerTaxCode.trim())
    ) {
      errors.customerTaxCode = BUYER_VALIDATION_MESSAGES.format.customerTaxCode
    }
  }

  if (buyerType === EINVOICE_BUYER_TYPE.INDIVIDUAL) {
    if (buyer.customerIdNumber?.trim()) {
      if (!BUYER_VALIDATION.ID_NUMBER.test(buyer.customerIdNumber.trim())) {
        errors.customerIdNumber = BUYER_VALIDATION_MESSAGES.format.customerIdNumber
      }
    }
    if (
      buyer.customerTaxCode?.trim() &&
      !BUYER_VALIDATION.TAX_CODE.test(buyer.customerTaxCode.trim())
    ) {
      errors.customerTaxCode = BUYER_VALIDATION_MESSAGES.format.customerTaxCode
    }
  }
  // Common validations
  // Cho phép nhiều email cách nhau dấu phẩy — kiểm tra từng email
  if (buyer.customerEmail?.trim() && !isValidEmailList(buyer.customerEmail)) {
    errors.customerEmail = BUYER_VALIDATION_MESSAGES.format.customerEmail
  }
  if (buyer.customerPhone?.trim() && !BUYER_VALIDATION.PHONE_VN.test(buyer.customerPhone.trim())) {
    errors.customerPhone = BUYER_VALIDATION_MESSAGES.format.customerPhone
  }

  return { valid: Object.keys(errors).length === 0, errors }
}

/**
 * Auto-detect buyer type from data.
 * @param {object} data
 * @returns {'ENTERPRISE'|'INDIVIDUAL'}
 */
function detectBuyerType(data) {
  const { customerTaxCode } = data

  // ENTERPRISE: Has valid tax code format
  if (customerTaxCode && BUYER_VALIDATION.TAX_CODE.test((customerTaxCode || '').trim())) {
    return EINVOICE_BUYER_TYPE.ENTERPRISE
  }

  // Default: INDIVIDUAL
  return EINVOICE_BUYER_TYPE.INDIVIDUAL
}

/**
 * Get error message for a required field.
 * @param {'ENTERPRISE'|'INDIVIDUAL'} buyerType
 * @param {string} field
 * @param {'required'|'format'} errorType
 * @returns {string}
 */
function getFieldErrorMessage(buyerType, field, errorType) {
  const messages = {
    ENTERPRISE: {
      customerName: 'Vui lòng nhập tên doanh nghiệp',
      customerTaxCode: 'Vui lòng nhập mã số thuế',
      customerAddress: 'Vui lòng nhập địa chỉ doanh nghiệp',
      customerEmail: 'Vui lòng nhập email nhận hóa đơn',
    },
    INDIVIDUAL: {
      customerName: 'Vui lòng nhập họ tên người mua',
    },
  }
  return messages[buyerType]?.[field] || `Vui lòng nhập ${field}`
}

/**
 * Returns true if the tax code format is structurally valid.
 * @param {string} taxCode
 * @returns {boolean}
 */
function isValidTaxCodeFormat(taxCode) {
  return BUYER_VALIDATION.TAX_CODE.test((taxCode || '').trim())
}

/**
 * Create empty buyer data object for a given buyer type.
 * @param {'ENTERPRISE'|'INDIVIDUAL'} buyerType
 * @returns {object}
 */
function createEmptyBuyerData(buyerType) {
  const baseFields = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  }

  switch (buyerType) {
    case EINVOICE_BUYER_TYPE.ENTERPRISE:
      return {
        ...baseFields,
        customerTaxCode: '',
        customerAddress: '',
        buyerName: '',
      }
    case EINVOICE_BUYER_TYPE.INDIVIDUAL:
      return {
        ...baseFields,
        customerIdNumber: '',
        customerTaxCode: '',
        customerAddress: '',
      }
    default:
      return baseFields
  }
}

/**
 * Normalize canonical bill identifiers for e-invoice flows.
 * Removes empty values and de-duplicates while preserving first-seen order.
 *
 * @param {Array<string|number>} billIds
 * @returns {Array<string|number>}
 */
function normalizeBillIds(billIds) {
  if (!Array.isArray(billIds)) {
    return []
  }

  return billIds.filter((billId) => billId !== null && billId !== undefined && billId !== '')
}

/**
 * Convert backend draft validation payload into a user-facing message.
 *
 * @param {{ message?: string, errors?: Array<string|{ message?: string }> }} validationResult
 * @returns {string}
 */
function formatDraftValidationMessage(validationResult) {
  const errors = Array.isArray(validationResult?.errors)
    ? validationResult.errors
        .map((item) => (typeof item === 'string' ? item : item?.message))
        .filter(Boolean)
    : []

  if (errors.length) {
    return errors.join('; ')
  }

  return validationResult?.message || 'Nháp HĐĐT chưa hợp lệ.'
}

/**
 * Build a failed-result item when single draft validation returns isValid=false.
 *
 * @param {{ detail?: object, salesInvoiceId: string|number, validationResult?: object }} params
 * @returns {{ billId: string|number, draftId: number|null, billCode: string|number, reasonCode: string, errorMessage: string, message: string }}
 */
function createSingleDraftValidationFailure(params = {}) {
  const detail = params.detail || {}
  const salesInvoiceId = detail.salesInvoiceId || params.salesInvoiceId
  const errorMessage = formatDraftValidationMessage(params.validationResult)

  return {
    billId: salesInvoiceId,
    draftId: detail.id || null,
    billCode: detail.saleInvoiceCode || salesInvoiceId,
    reasonCode: 'DRAFT_VALIDATE_FAILED',
    errorMessage,
    message: errorMessage,
  }
}

function normalizeRetailFacetBuyerField(value) {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value).trim()
}

function createBuyerDataFromRetailFacetBuyer(source = {}) {
  return {
    customerName: normalizeRetailFacetBuyerField(source.buyerName),
    customerEmail: normalizeRetailFacetBuyerField(source.buyerEmail),
    customerPhone: normalizeRetailFacetBuyerField(source.buyerPhone),
    customerTaxCode: normalizeRetailFacetBuyerField(source.buyerTaxCode),
    customerAddress: normalizeRetailFacetBuyerField(source.buyerAddress),
    customerIdNumber: '',
    buyerName: '',
  }
}

function createRetailBuyerDrafts(source = {}) {
  return {
    [EINVOICE_BUYER_TYPE.ENTERPRISE]: createBuyerDataFromRetailFacetBuyer(source.buyerCompany),
    [EINVOICE_BUYER_TYPE.INDIVIDUAL]: createBuyerDataFromRetailFacetBuyer(source.buyerIndividual),
  }
}

export {
  bucketizeItems,
  buildDraftOverride,
  computeIsSingleSilent,
  computePollingInterval,
  validateBuyerInfo,
  isValidTaxCodeFormat,
  detectBuyerType,
  getFieldErrorMessage,
  createEmptyBuyerData,
  createBuyerDataFromRetailFacetBuyer,
  createRetailBuyerDrafts,
  normalizeBillIds,
  formatDraftValidationMessage,
  createSingleDraftValidationFailure,
}
